import React, { useState, useRef, useCallback } from 'react';
import { useField } from 'formik';
import { Icon, Button, Confirm, Wysiwyg, Uploader, HtmlContent, insertLink, insertImage } from '..';
import type { WysiwygHandle } from '../Wysiwyg';
import { editorStateFromHtml } from '../Wysiwyg';
import { Attachment } from '../../models';
import { EditorState } from 'draft-js';
import { useTranslation } from 'react-i18next';
import type { Mention } from '..';

interface WysiwygFieldProps {
    name: string;
    label?: JSX.Element;
    rows?: number;
    id?: string;
    inline?: boolean;
    placeholder?: string;
    required?: boolean;
    className?: string;
    containerClassName?: string;
    contentStyle?: React.CSSProperties;
    onClose?: () => void;
    onChange?: (value: string) => void;
    onSave: (value: string) => void;
    onDelete?: () => void;
    children?: string | JSX.Element | JSX.Element[] | (() => JSX.Element);
    editOnClick?: boolean;
    hideButtons?: boolean;
    saveTitle?: string;
    saveIcon?: string;
    smallButtons?: boolean;
    hideCancelButton?: boolean;
    minimal?: boolean;
    isLoading: boolean;
    mentions?: Mention[];
    uploadProps?: {
        projectId?: string;
        changeId?: string;
    };
    uploadHideFiles?: boolean;
    projectId?: string;
    /**
     * Optional draft that survives Formik `enableReinitialize`.
     * When set (including ''), the editor is driven by this value instead of the Formik field.
     */
    draftValue?: string;
    onDraftChange?: (value: string) => void;
};
const WysiwygField = (props: WysiwygFieldProps) => {
    const {
        name, className, containerClassName, contentStyle, rows, children, editOnClick, minimal, uploadProps,
        label, inline, placeholder, isLoading, onClose, onChange, onSave, onDelete, hideButtons, mentions, saveTitle, saveIcon, smallButtons, hideCancelButton, uploadHideFiles, projectId,
        draftValue, onDraftChange,
    } = props;

    const { t } = useTranslation();

    const [ isEditing, setIsEditing ]           = useState(!inline);
    const [confirmIsVisible, setConfirmVisible] = useState(false);
    const wysiwygRef                            = useRef<WysiwygHandle>(null);
    const editorStateRef                        = useRef<EditorState | undefined>(undefined);

    const [ , meta, helpers ]  = useField(name);
    const { setValue }              = helpers;

    const { initialValue, value, touched, error } = meta;
    const useDraft = draftValue !== undefined;
    const displayValue = useDraft ? draftValue : value;

    const applyValue = useCallback((next: string) => {
        if (onDraftChange)
            onDraftChange(next);
        else
            setValue(next);
    }, [onDraftChange, setValue]);

    const _onChange = React.useMemo(() => {
        return (nextValue: string, nextEditorState: EditorState) => {
            editorStateRef.current = nextEditorState;
            applyValue(nextValue);
            onChange && onChange(nextValue);
        };
    }, [applyValue, onChange]);

    const _onClose = () => {
        setIsEditing(false);
        editorStateRef.current = undefined;
        if (onDraftChange)
            onDraftChange('');
        else
            setValue(initialValue);
        onClose && onClose();
    }

    React.useMemo(() => {
        if (isLoading === false)
            setIsEditing(!inline);
    }, [isLoading]);

    const insertAttachment = (attachment: Attachment) => {
        const baseState = editorStateRef.current
            || (displayValue ? editorStateFromHtml(displayValue) : EditorState.createEmpty());

        const newState = attachment.isPicture() ?
            insertImage(baseState, attachment.downloadUrl(), attachment.name) :
            insertLink(baseState, attachment.name, attachment.downloadUrl());
        if (!newState)
            return;

        editorStateRef.current = newState;
        wysiwygRef.current?.applyEditorState(newState);
    }

    if (!isEditing)
        return (
            <div className={`flex flex-col justify-between gap-0 ${containerClassName}`}>
                {(label || displayValue) && (
                    <div className="mb-1 flex justify-between">
                        {label}
                        {displayValue && (
                            <div className="flex">
                                <div className="flex items-center text-xs cursor-pointer font-normal border-none text-gray-500 dark:text-gray-400" onClick={() => setIsEditing(true)}>
                                    <Icon type="edit" className="inline mr-0.5" size={3} />
                                    <span className="hover:underline">
                                        {t('common.edit')}
                                    </span>
                                </div>
                                {onDelete && (
                                    <div className="flex items-center text-xs cursor-pointer font-normal border-none text-gray-500 dark:text-gray-400" onClick={() => setConfirmVisible(true)}>
                                        <Icon type="delete" className="inline ml-3 mr-0.5" size={3} />
                                        <span className="hover:underline">
                                            {t('common.delete')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <div onClick={() => editOnClick && setIsEditing(true)} className="cursor-pointer">
                    {displayValue ? (
                        <div
                            className={`Wysiwyg content border border-gray-100 dark:border-gray-700 p-2 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${editOnClick ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''} text-gray-500 rounded text-sm`}
                            style={contentStyle}
                        >
                            <HtmlContent html={displayValue} projectId={projectId || uploadProps?.projectId} />
                        </div>
                    ) : (
                        <div
                            className="border border-gray-100 dark:border-gray-700 p-2 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 italic rounded text-sm"
                            style={{
                                minHeight: rows ? `${rows * 2}rem` : undefined,
                                ...contentStyle,
                            }}
                        >
                            <div>{ placeholder }</div>
                        </div>
                    )}
                </div>
                {onDelete && (
                    <Confirm
                        visible={confirmIsVisible}
                        onConfirm={() => onDelete()}
                        onCancel={() => setConfirmVisible(false)}
                        title={t('changes.are_you_sure_to_delete_comment') || ''}
                    />
                )}
            </div>
        );

    return (
        <>
            <Wysiwyg
                ref={wysiwygRef}
                value={displayValue}
                onChange={_onChange}
                onEditorStateChange={(nextState) => { editorStateRef.current = nextState; }}
                className={className}
                rows={rows}
                minimal={minimal}
                mentions={mentions}
            >
                <div>
                    <>{ children }</>
                    {uploadProps?.projectId && (
                        <Uploader
                            projectId={uploadProps.projectId}
                            changeId={uploadProps.changeId}
                            onUpload={insertAttachment}
                            hideFiles={uploadHideFiles !== false}
                        />
                    )}
                    {!hideButtons && (
                        <div className="flex justify-end mt-2 bg-gray-50 dark:bg-gray-900 p-2 order border-gray-100 dark:border-gray-700 rounded-md">
                            {!hideCancelButton && (
                                <Button
                                    className="text-2xl font-bold"
                                    textColor="red-500"
                                    icon="close"
                                    color="none"
                                    onClick={_onClose}
                                    title={t('common.cancel')}
                                    iconColor="red-500"
                                    small={smallButtons !== false}
                                />
                            )}
                            <Button
                                icon={isLoading ? 'loading' : ((saveIcon || 'save') as any)}
                                color="primary"
                                onClick={() => { onSave && onSave(displayValue); _onClose(); }}
                                type="button"
                                title={saveTitle || t('common.save')}
                                small={smallButtons !== false}
                            />
                        </div>
                    )}
                </div>
            </Wysiwyg>
            {touched && error ? (
                <div className="error">{error}</div>
            ) : null}
        </>
    );
};

export default WysiwygField;
